'use client';

import React from 'react';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Avatar,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import {
  LogoutOutlined as LogoutIcon,
  ScheduleOutlined as ScheduleIcon,
} from '@mui/icons-material';
import { signOut } from 'next-auth/react';
import { useAuth } from '@/hooks/useAuth';
import { useTenant } from '@/contexts/TenantContext';
import { AUTH_CONFIG } from '@/config/app';

interface TenantMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

/**
 * Tenant Menu Component
 *
 * Displays a popover menu when clicking the tenant item in the sidebar footer.
 * Matches the Figma design with user info, tenant switcher, timezone, and logout.
 */
export function TenantMenu({ anchorEl, open, onClose }: TenantMenuProps) {
  const { user } = useAuth();
  const { selectedTenant, availableTenants, setSelectedTenant } = useTenant();

  const handleTenantChange = (event: SelectChangeEvent<string>) => {
    const newTenant = event.target.value;
    if (newTenant && newTenant !== selectedTenant) {
      setSelectedTenant(newTenant);
      window.location.reload();
    }
  };

  const handleLogout = () => {
    onClose();
    signOut({ callbackUrl: AUTH_CONFIG.signOutCallbackUrl });
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (user?.name) {
      const parts = user.name.split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return user.name.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  // Get timezone info
  const getTimezoneInfo = () => {
    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return timeZone.replace(/_/g, ' ');
    } catch {
      return 'Unknown Timezone';
    }
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      transformOrigin={{ horizontal: 'left', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      slotProps={{
        paper: {
          sx: {
            minWidth: 246,
            bgcolor: 'var(--sidebar-bg)',
            color: 'white',
            borderRadius: 'var(--radius-md)',
            ml: 1, // Gap between sidebar and popup (8px)
            mt: 1, // Top margin to create visual gap
          },
        },
      }}
      PaperProps={{
        elevation: 8,
      }}
      MenuListProps={{
        sx: {
          p: 1,
        },
      }}
    >
      {/* User Info Section */}
      <Box sx={{ px: 0, py: 0.5, mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'white',
            color: 'black',
          }}
        >
          <Typography variant="body3SemiBold">
            {getUserInitials()}
          </Typography>
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2SemiBold"
            sx={{
              color: 'white',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {user?.name || 'User'}
          </Typography>
          <Typography
            variant="body3"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
          >
            {selectedTenant || 'Demonstration Tenant'}
          </Typography>
        </Box>
      </Box>

      {/* Tenant Switcher */}
      <Box sx={{ px: 0, py: 0.5, mb: 1 }}>
        <FormControl fullWidth size="small">
          <InputLabel
            id="tenant-select-label"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              '&.Mui-focused': {
                color: 'rgba(255, 255, 255, 0.9)',
              },
            }}
          >
            Switch Client/Tenant
          </InputLabel>
          <Select
            labelId="tenant-select-label"
            value={selectedTenant || ''}
            onChange={handleTenantChange}
            label="Switch Client/Tenant"
            sx={{
              color: 'white',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
              },
              '& .MuiSvgIcon-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: 'var(--sidebar-bg)',
                  color: 'white',
                  '& .MuiMenuItem-root': {
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.08)',
                    },
                    '&.Mui-selected': {
                      bgcolor: 'rgba(255, 255, 255, 0.12)',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.16)',
                      },
                    },
                  },
                },
              },
            }}
          >
            {availableTenants.map((tenant) => (
              <MenuItem key={tenant} value={tenant}>
                {tenant}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Timezone Info */}
      <MenuItem
        sx={{
          pl: 1.5,
          pr: 0,
          py: 0.5,
          color: 'white',
          '&:hover': {
            bgcolor: 'rgba(255, 255, 255, 0.08)',
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 10, ml: 0, pr: 0 }}>
          <ScheduleIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1rem' }} />
        </ListItemIcon>
        <ListItemText
          primary={getTimezoneInfo()}
          primaryTypographyProps={{
            variant: 'body2',
            color: 'white',
          }}
        />
      </MenuItem>

      {/* Logout */}
      <MenuItem
        onClick={handleLogout}
        sx={{
          pl: 1.5,
          pr: 0,
          py: 0.5,
          color: 'white',
          '&:hover': {
            bgcolor: 'rgba(255, 255, 255, 0.08)',
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 10, ml: 0, pr: 0 }}>
          <LogoutIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1rem' }} />
        </ListItemIcon>
        <ListItemText
          primary="Logout"
          primaryTypographyProps={{
            variant: 'body2',
            color: 'white',
          }}
        />
      </MenuItem>
    </Menu>
  );
}
